// src/pages/purchaseOrder/components/PurchaseOrderPaymentRecordsModal.jsx
//
// Supplier payments for a purchase order — the shared PaymentRecordsModal with no
// verification flow, debit notes (same supplier) as the offset, and SupplierPayments as storage.
import React, { useMemo } from 'react';
import {
  getPaymentsForPurchaseOrder,
  recordPurchaseOrderPayments,
  updateSupplierPayment,
  getProcurementPaymentMethods,
} from '../../../services/api/procurement/supplierPayment';
import { getDebitNotes, applyDebitNote } from '../../../services/api/procurement/debitNote';
import { getPurchaseOrderById } from '../../../services/api/purchaseOrder';
import { uploadOrderPaymentProof } from '../../../services/api/orders';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { usePermissions } from '../../../hooks/usePermissions';
import PaymentRecordsModal from '../../../components/common/PaymentRecordsModal/PaymentRecordsModal';

const normalizePayment = (payment) => ({
  ...payment,
  paymentDate: payment.paymentDate ? String(payment.paymentDate).split('T')[0] : '',
  isVerified: false,
  readOnly: !!payment.debitNoteId,
});

export const getPurchaseOrderBalance = (purchaseOrder) =>
  Math.max(0, parseFloat(purchaseOrder.grandTotal || 0) - parseFloat(purchaseOrder.paidAmount || 0));

const PurchaseOrderPaymentRecordsModal = ({ purchaseOrder, onClose, onSaved }) => {
  const { t } = useLocalization();
  const { hasPermission } = usePermissions();
  const canRecordPayments = hasPermission('create_supplier_payment');
  const canApplyDebitNote = hasPermission('issue_debit_note');

  const adapter = useMemo(() => ({
    inputIdPrefix: 'po',
    canEditRecords: canRecordPayments,
    getNumber: (entity) => entity.purchaseOrderNumber,
    getBalance: (entity) => (canRecordPayments || canApplyDebitNote ? getPurchaseOrderBalance(entity) : 0),
    getPartyId: (entity) => entity.supplierId || entity.Supplier?.id,
    loadRecords: async (purchaseOrderId) => {
      const response = await getPaymentsForPurchaseOrder(purchaseOrderId);
      return (response.payments || []).map(normalizePayment);
    },
    updateRecord: async (id, data) => normalizePayment((await updateSupplierPayment(id, data)).payment),
    recordPayments: (purchaseOrderId, payments) => recordPurchaseOrderPayments(purchaseOrderId, payments),
    reloadEntity: async (purchaseOrderId) => (await getPurchaseOrderById(purchaseOrderId)).purchaseOrder,
    loadMethods: canRecordPayments ? getProcurementPaymentMethods : async () => [],
    uploadProof: uploadOrderPaymentProof,
    verification: null,
    offset: {
      key: 'debit_note_apply',
      label: t('purchaseOrder.debitNotePaymentMethod'),
      iconFileName: 'debit-note.svg',
      numberField: 'debitNoteNumber',
      canApply: canApplyDebitNote,
      loadNotes: async (supplierId) => (await getDebitNotes({ supplierId, limit: 200 })).debitNotes || [],
      applyNote: (noteId, purchaseOrderId, amount) => applyDebitNote(noteId, { purchaseOrderId, amount }),
      texts: {
        select: t('purchaseOrder.selectDebitNote'),
        search: t('purchaseOrder.searchDebitNote'),
        none: t('purchaseOrder.noAvailableDebitNotes'),
        applyAmount: t('debitNote.applyAmountLabel'),
        amountMustBePositive: t('debitNote.amountMustBePositive'),
        exceedsRemaining: t('debitNote.applyAmountExceedsRemaining'),
        exceedsBalance: t('debitNote.applyAmountExceedsPurchaseOrderBalance'),
      },
    },
    texts: {
      title: t('purchaseOrder.paymentDetails'),
      outstandingBalance: t('purchaseOrder.outstandingBalance'),
      amount: t('purchaseOrder.paymentAmount'),
      reference: t('purchaseOrder.paymentReference'),
      confirmPayment: t('purchaseOrder.confirmPayment'),
      someFieldsRequired: t('purchaseOrder.completePaymentFields'),
      paymentRecordedSuccess: t('purchaseOrder.paymentRecordedSuccess'),
      failedToRecordPayment: t('purchaseOrder.failedToRecordPayment'),
    },
  }), [t, canRecordPayments, canApplyDebitNote]);

  return <PaymentRecordsModal entity={purchaseOrder} adapter={adapter} onClose={onClose} onSaved={onSaved} />;
};

export default PurchaseOrderPaymentRecordsModal;
